import React, { useCallback, useRef, useState } from "react";
import { Box, CircularProgress, Skeleton } from "@mui/material";

// Counts overlapping requests without changing when or how they are sent.
export function useLoadingState(initialLoading = false) {
  const pending = useRef(0);
  const [loading, setLoading] = useState(initialLoading);
  const updateLoading = useCallback((active) => {
    pending.current = Math.max(0, pending.current + (active ? 1 : -1));
    setLoading(pending.current > 0);
  }, []);
  return [loading, updateLoading];
}

export default function LoadingFeedback({ loading, children, variant = "table", rows = 5, preserveLayout = false }) {
  if (loading === undefined) return <>{children}</>;
  const isChart = variant === "chart";
  const keepLayout = isChart || preserveLayout;
  return (
    <Box aria-busy={loading} sx={{ position: "relative", minWidth: 0 }}>
      <Box aria-hidden={loading || undefined} inert={loading ? "" : undefined}
        sx={keepLayout ? { visibility: loading ? "hidden" : "visible", minHeight: isChart ? 280 : undefined }
          : { display: loading ? "none" : "contents" }}>
        {children}
      </Box>
      {loading && (
        <Box role="status" aria-label="Procesando" sx={{ width: "100%", py: 1,
          ...(keepLayout ? { position: "absolute", inset: 0 } : {}),
          ...(preserveLayout && variant === "spinner" ? { display: "flex", alignItems: "center", justifyContent: "center" } : {}) }}>
          {variant === "spinner" ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : isChart ? (
            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
          ) : (
            <>
              <Skeleton variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
              {Array.from({ length: rows }, (_, index) => (
                <Box key={index} sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 2, mb: 1 }}>
                  {[0, 1, 2].map((column) => <Skeleton key={column} variant="rounded" height={32} />)}
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
